import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { BaseFieldProps, NakedButton, useTooltip } from '@frontend/design-system';

const radioSelectionOptionNakedButtonStyle1 = css`
  display: flex;
  align-items: center;
  border-radius: ${theme.borderRadius.small};
`;

const focusWithinStyle = css`
  &:focus-within {
    outline: 2px solid ${theme.colors.primary50};
    outline-offset: 2px;
  }
`;

const radioSelectionOptionInputStyle2 = css`
  height: 0px;
  width: 0px;
`;

const radioSelectionOptionTooltipStyle3 = css`
  max-width: unset;
  font-weight: ${theme.font.weight.regular};
`;

const radioSelectionChipDivStyle4 = css`
  display: flex;
  column-gap: ${theme.spacing(1)};
`;

type SelectionChipOption = {
  value: string;
  label: string;
  disabled?: boolean;
  trackingId?: string;
};

const TOOLTIP_VISIBLE_CHARACTER_LENGTH = 18;

const RadioSelectionOption = ({
  fieldProps,
  option,
  readOnly,
  size,
}: {
  fieldProps: BaseFieldProps & { name: string };
  option: SelectionChipOption;
  readOnly: boolean;
  size: 'small' | 'large';
}) => {
  const isSmall = size === 'small';

  const checkIsValue = (value: string) => fieldProps.value == value;
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ placement: 'top', trigger: 'hover' });

  return (
    <NakedButton
      tabIndex={readOnly || option.disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          fieldProps.onChange({ name: fieldProps.name, value: option.value });
        }
      }}
      className={cx(
        radioSelectionOptionNakedButtonStyle1,
        checkIsValue(option.value) ? 'selected-chip' : 'unselected-chip',
        !(readOnly || option.disabled) && focusWithinStyle
      )}
    >
      <input
        tabIndex={-1}
        type='radio'
        {...fieldProps}
        id={option.value}
        value={option.value}
        className={radioSelectionOptionInputStyle2}
        disabled={readOnly || option.disabled}
        checked={checkIsValue(option.value)}
      />
      <label
        tabIndex={-1}
        htmlFor={option.value}
        data-trackingid={option.trackingId}
        css={{
          display: 'block',
          borderRadius: theme.borderRadius.small,
          columnGap: theme.spacing(1),
          fontSize: isSmall ? theme.fontSize(14) : theme.fontSize(12),
          padding: isSmall ? theme.spacing(0.5, 1) : theme.spacing(1),
          maxWidth: isSmall ? theme.spacing(18) : 'unset',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(readOnly || option.disabled
            ? checkIsValue(option.value)
              ? disabledSelected
              : disabledUnselected
            : checkIsValue(option.value)
            ? selected
            : unselected),
        }}
        {...(option.label.length > TOOLTIP_VISIBLE_CHARACTER_LENGTH ? { ...triggerProps } : {})}
      >
        {option.label}
      </label>
      <Tooltip {...tooltipProps} className={radioSelectionOptionTooltipStyle3}>
        {option.label}
      </Tooltip>
    </NakedButton>
  );
};

type Props = {
  fieldProps: BaseFieldProps & { name: string };
  options: SelectionChipOption[];
  readOnly?: boolean;
  size?: 'small' | 'large';
  className?: string;
};

export const RadioSelectionChip = ({
  fieldProps,
  options,
  readOnly = false,
  size = 'large',
  className,
  ...rest
}: Props) => {
  return (
    <div {...rest} className={cx(radioSelectionChipDivStyle4, className)}>
      {options.map((option) => (
        <RadioSelectionOption key={option.value} {...{ fieldProps, option, readOnly, size }} />
      ))}
    </div>
  );
};

const selected = {
  cursor: 'pointer',
  backgroundColor: theme.colors.primary5,
};

const unselected = {
  cursor: 'pointer',
  border: `1px solid ${theme.colors.neutral50}`,
  '&:hover': {
    backgroundColor: theme.colors.neutral10,
    border: `1px solid transparent`,
  },
};

const disabledSelected = {
  cursor: 'not-allowed',
  background: theme.colors.primary5,
  color: theme.colors.neutral90,
};

const disabledUnselected = {
  cursor: 'not-allowed',
  background: theme.colors.neutral5,
  border: `1px solid ${theme.colors.neutral20}`,
  color: theme.colors.neutral90,
};
